import React, { useState } from 'react';
import { ParticipantAvatar, UserSelector } from '../../../components/shared';
import { useLanguage } from '../../../hooks/shared';
import { Participant } from '../types/calendarTypes';
import ActionTooltip from './ActionTooltip';

interface TemplateParticipantCardProps {
  participant: Participant;
  onReplace: (fromParticipantId: string, toUserId: string) => Promise<void>;
  onDelete: (participantId: string) => Promise<void>;
  availableUsersForReplace: Array<{
    id: string;
    name: string;
    photoUrl?: string;
  }>;
  isFirst?: boolean;
}

/**
 * Simplified participant card for template editing.
 * Differences from ParticipantCard:
 * - No confirm action (templates don't have confirmation status)
 * - No cancel action (templates don't have cancellation)
 * - Replace form shows only the new participant (no "replacing X" info)
 */
const TemplateParticipantCard: React.FC<TemplateParticipantCardProps> = ({
  participant,
  onReplace,
  onDelete,
  availableUsersForReplace,
  isFirst = false,
}) => {
  const { t } = useLanguage();
  const [showReplaceForm, setShowReplaceForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isReplacing, setIsReplacing] = useState(false);

  const handleReplace = async () => {
    if (!selectedUserId) return;
    setIsReplacing(true);
    try {
      await onReplace(participant.id, selectedUserId);
      setShowReplaceForm(false);
      setSelectedUserId('');
    } catch (error) {
      console.error('Failed to replace template participant:', error);
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <div
      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        {/* Participant info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <ParticipantAvatar
            name={participant.name}
            photoUrl={participant.photoUrl}
          />
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-900 truncate block">
              {participant.name}
            </span>
          </div>
        </div>

        {/* Action buttons - only replace and delete for templates */}
        {!showReplaceForm && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Replace button - opens form directly */}
            <button
              type="button"
              onClick={() => setShowReplaceForm(true)}
              className="p-2 rounded transition-colors text-blue-600 hover:bg-blue-100 hover:text-blue-700"
              title={t('calendar.replaceParticipant')}
            >
              <span className="text-lg">↔</span>
            </button>

            {/* Delete button */}
            <ActionTooltip
              label={t('common.delete')}
              disabled={false}
              onConfirm={async () => {
                await onDelete(participant.id);
              }}
              variant="danger"
              openDown={isFirst}
            >
              <button
                type="button"
                className="p-2 rounded transition-colors text-red-600 hover:bg-red-100 hover:text-red-700"
              >
                <span className="text-lg">🗑</span>
              </button>
            </ActionTooltip>
          </div>
        )}
      </div>

      {/* Replace form - simplified version without showing original participant */}
      {showReplaceForm && (
        <div className="mt-3 pt-3 border-t border-gray-300 space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('calendar.newParticipant')}
            </label>
            <UserSelector
              users={availableUsersForReplace}
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder={t('calendar.selectParticipant')}
              disabled={isReplacing}
              dropdownDirection={isFirst ? 'down' : 'up'}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowReplaceForm(false);
                setSelectedUserId('');
              }}
              disabled={isReplacing}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleReplace}
              disabled={!selectedUserId || isReplacing}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReplacing ? t('common.saving') : t('common.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateParticipantCard;
